import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useFeatureFlag from "app/hooks/useFeatureFlag";
import useFeatureFlagWithContext from "app/hooks/useFeatureFlagWithContext";
import { FEATURE_FLAGS } from "app/constants/FeatureFlag";
import {
  getSubscriptionPlans,
  perfilAdapter,
} from "app/hooks/useSubscriptionPlans";
import {
  PRICE_BASE_UNIVERSAL_STARTER_MONTHLY,
  PRICE_BASE_UNIVERSAL_STARTER_ANNUAL,
  PRICE_BASE_UNIVERSAL_MASTER_MONTHLY,
  PRICE_BASE_UNIVERSAL_MASTER_ANNUAL,
  PRICE_BASE_UNIVERSAL_ULTIMATE_MONTHLY,
  PRICE_BASE_UNIVERSAL_ULTIMATE_ANNUAL,
} from "app/components/ModalSubscriptionReached/constants";

const BILLING_CYCLES = {
  MONTHLY: "monthly",
  ANNUAL: "annual",
};

const PLAN_TYPES = {
  STARTER: "starter",
  MASTER: "master",
  ULTIMATE: "ultimate",
};

const normalizeCycleForFindPlanOptions = (cycle) => {
  if (cycle === "annual") {
    return "annually";
  }
  return cycle;
};

// Utilitário para formatar preço em BRL
const formatBRL = value =>
  `R$ ${value
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;

// Fonte de verdade: só totalValue
const BASE_PRICING = {
  [PLAN_TYPES.STARTER]: {
    monthly: { title: "PLANO STARTER", totalValue: PRICE_BASE_UNIVERSAL_STARTER_MONTHLY },
    annual: { title: "PLANO STARTER", totalValue: PRICE_BASE_UNIVERSAL_STARTER_ANNUAL },
  },
  [PLAN_TYPES.MASTER]: {
    monthly: { title: "PLANO MASTER", totalValue: PRICE_BASE_UNIVERSAL_MASTER_MONTHLY },
    annual: { title: "PLANO MASTER", totalValue: PRICE_BASE_UNIVERSAL_MASTER_ANNUAL },
  },
  [PLAN_TYPES.ULTIMATE]: {
    monthly: { title: "PLANO ULTIMATE", totalValue: PRICE_BASE_UNIVERSAL_ULTIMATE_MONTHLY },
    annual: { title: "PLANO ULTIMATE", totalValue: PRICE_BASE_UNIVERSAL_ULTIMATE_ANNUAL },
  },
};

const JUSPROCESSOS_PRICING = {
  50: {
    [PLAN_TYPES.STARTER]: {
      monthly: {
        title: "PLANO STARTER + JUSPROCESSOS ATÉ 50 PROCESSOS",
        totalValue: 101.9,
      },
      annual: {
        title: "PLANO STARTER + JUSPROCESSOS ATÉ 50 PROCESSOS",
        totalValue: 1038.0,
      },
    },
    [PLAN_TYPES.MASTER]: {
      monthly: {
        title: "PLANO MASTER + JUSPROCESSOS ATÉ 50 PROCESSOS",
        totalValue: 121.9,
      },
      annual: {
        title: "PLANO MASTER + JUSPROCESSOS ATÉ 50 PROCESSOS",
        totalValue: 1230.0,
      },
    },
    [PLAN_TYPES.ULTIMATE]: {
      monthly: {
        title: "PLANO ULTIMATE + JUSPROCESSOS ATÉ 50 PROCESSOS",
        totalValue: 141.9,
      },
      annual: {
        title: "PLANO ULTIMATE + JUSPROCESSOS ATÉ 50 PROCESSOS",
        totalValue: 1422.0,
      },
    },
  },
  200: {
    [PLAN_TYPES.STARTER]: {
      monthly: {
        title: "PLANO STARTER + JUSPROCESSOS ATÉ 200 PROCESSOS",
        totalValue: 176.9,
      },
      annual: {
        title: "PLANO STARTER + JUSPROCESSOS ATÉ 200 PROCESSOS",
        totalValue: 1938.0,
      },
    },
    [PLAN_TYPES.MASTER]: {
      monthly: {
        title: "PLANO MASTER + JUSPROCESSOS ATÉ 200 PROCESSOS",
        totalValue: 196.9,
      },
      annual: {
        title: "PLANO MASTER + JUSPROCESSOS ATÉ 200 PROCESSOS",
        totalValue: 2130.0,
      },
    },
    [PLAN_TYPES.ULTIMATE]: {
      monthly: {
        title: "PLANO ULTIMATE + JUSPROCESSOS ATÉ 200 PROCESSOS",
        totalValue: 216.9,
      },
      annual: {
        title: "PLANO ULTIMATE + JUSPROCESSOS ATÉ 200 PROCESSOS",
        totalValue: 2322.0,
      },
    },
  },
  500: {
    [PLAN_TYPES.STARTER]: {
      monthly: {
        title: "PLANO STARTER + JUSPROCESSOS ATÉ 500 PROCESSOS",
        totalValue: 326.9,
      },
      annual: {
        title: "PLANO STARTER + JUSPROCESSOS ATÉ 500 PROCESSOS",
        totalValue: 3738.0,
      },
    },
    [PLAN_TYPES.MASTER]: {
      monthly: {
        title: "PLANO MASTER + JUSPROCESSOS ATÉ 500 PROCESSOS",
        totalValue: 346.9,
      },
      annual: {
        title: "PLANO MASTER + JUSPROCESSOS ATÉ 500 PROCESSOS",
        totalValue: 3930.0,
      },
    },
    [PLAN_TYPES.ULTIMATE]: {
      monthly: {
        title: "PLANO ULTIMATE + JUSPROCESSOS ATÉ 500 PROCESSOS",
        totalValue: 366.9,
      },
      annual: {
        title: "PLANO ULTIMATE + JUSPROCESSOS ATÉ 500 PROCESSOS",
        totalValue: 4122.0,
      },
    },
  },
};

const BASE_PRICING_UNIVERSAL = {
  [PLAN_TYPES.STARTER]: {
    monthly: {
      title: "[PLANO BASE 2026] STARTER",
      totalValue: 77.0,
    },
    annual: {
      title: "[PLANO BASE 2026]  Starter Anual Parcelado 20% OFF",
      totalValue: 739.2,
    },
  },
  [PLAN_TYPES.MASTER]: {
    monthly: {
      title: "[PLANO BASE 2026] MASTER ",
      totalValue: 97.0,
    },
    annual: {
      title: "[Promoção 2026] Master Anual Parcelado 20% OFF",
      totalValue: 931.2,
    },
  },
  [PLAN_TYPES.ULTIMATE]: {
    monthly: {
      title: "[PLANO BASE 2026] ULTIMATE",
      totalValue: 117.0,
    },
    annual: {
      title: "[PLANO BASE 2026] Ultimate Anual Parcelado 20% OFF",
      totalValue: 1123.2,
    },
  },
};
const JUSPROCESSOS_PRICING_UNIVERSAL = {
  50: {
    [PLAN_TYPES.STARTER]: {
      monthly: {
        title: `[PLANO BASE 2026] STARTER + JusProcessos até 50 processos`,
        totalValue: 101.9,
      },
      annual: {
        title: `[PLANO BASE 2026]  STARTER ANUAL 20% + JusProcessos até 50 processos`,
        totalValue: 1038.0,
      },
    },
    [PLAN_TYPES.MASTER]: {
      monthly: {
        title: `[PLANO BASE 2026] MASTER + JusProcessos até 50 processos`,
        totalValue: 121.9,
      },
      annual: {
        title: `[PROMOÇÃO 2026] MASTER ANUAL 20% + JusProcessos até 50 processos`,
        totalValue: 1230.0,
      },
    },
    [PLAN_TYPES.ULTIMATE]: {
      monthly: {
        title: `[PLANO BASE 2026] ULTIMATE + JusProcessos até 50 processos`,
        totalValue: 141.9,
      },
      annual: {
        title: `[PROMOÇÃO 2026] ULTIMATE ANUAL 20% + JusProcessos até 50 processos`,
        totalValue: 1422.0,
      },
    },
  },

  200: {
    [PLAN_TYPES.STARTER]: {
      monthly: {
        title: `[PLANO BASE 2026] STARTER + JusProcessos até 200 processos`,
        totalValue: 176.9,
      },
      annual: {
        title: `[PLANO BASE 2026] STARTER ANUAL 20%  + JUSPROCESSOS ATÉ 200 PROCESSOS`,
        totalValue: 1938.0,
      },
    },
    [PLAN_TYPES.MASTER]: {
      monthly: {
        title: `[PLANO BASE 2026] MASTER + JUSPROCESSOS ATÉ 200 PROCESSOS`,
        totalValue: 196.9,
      },
      annual: {
        title: `[PLANO BASE 2026] MASTER ANUAL 20% + JUSPROCESSOS ATÉ 200 PROCESSOS`,
        totalValue: 2130.0,
      },
    },
    [PLAN_TYPES.ULTIMATE]: {
      monthly: {
        title: `[PLANO BASE 2026] ULTIMATE + JUSPROCESSOS ATÉ 200 PROCESSOS`,
        totalValue: 216.9,
      },
      annual: {
        title: `[PLANO BASE 2026] ULTIMATE ANUAL 20%  + JUSPROCESSOS ATÉ 200 PROCESSOS`,
        totalValue: 2322.0,
      },
    },
  },
  500: {
    [PLAN_TYPES.STARTER]: {
      monthly: {
        title: `[PLANO BASE 2026] STARTER + JUSPROCESSOS ATÉ 500 PROCESSOS`,
        totalValue: 326.9,
      },
      annual: {
        title: `[PLANO BASE 2026] STARTER ANUAL 20%  + JUSPROCESSOS ATÉ 500 PROCESSOS`,
        totalValue: 3738.0,
      },
    },
    [PLAN_TYPES.MASTER]: {
      monthly: {
        title: `[PLANO BASE 2026] MASTER + JUSPROCESSOS ATÉ 500 PROCESSOS`,
        totalValue: 346.9,
      },
      annual: {
        title: `[PLANO BASE 2026] MASTER ANUAL 20%  + JUSPROCESSOS ATÉ 500 PROCESSOS`,
        totalValue: 3930.0,
      },
    },
    [PLAN_TYPES.ULTIMATE]: {
      monthly: {
        title: `[PLANO BASE 2026] ULTIMATE  + JUSPROCESSOS ATÉ 500 PROCESSOS`,
        totalValue: 366.9,
      },
      annual: {
        title: `[PLANO BASE 2026] ULTIMATE ANUAL 20%  + JUSPROCESSOS ATÉ 500 PROCESSOS`,
        totalValue: 4122.0,
      },
    },
  },
};

// Função utilitária para montar objeto de exibição
function getPlanDisplayData(plan, cycle) {
  const { title, totalValue } = plan[cycle];
  if (cycle === "monthly") {
    return {
      title,
      price: `${formatBRL(totalValue)}`,
      total: formatBRL(totalValue),
      totalValue,
      priceValue: totalValue,
    };
  }
  if (cycle === "annual") {
    return {
      title,
      price: `${formatBRL(totalValue)}`,
      total: `(totalizando ${formatBRL(totalValue)})`,
      totalValue,
      priceValue: totalValue,
    };
  }
  throw new Error("Tipo de ciclo inválido");
}

/**
 * @deprecated This hook uses hardcoded plan values and will be replaced by centralized subscription plans.
 * Use the new getSubscriptionPlans function from hooks/useSubscriptionPlans when the feature flag is enabled.
 */
function useSubscriptionPlans(
  hasJusProcessos = false,
  processCount = 200,
  isUniversal = false
) {
  const user = useSelector(state => state.auth.user);
  const { isFeatureFlagEnable, flagResolved } = useFeatureFlag(
    FEATURE_FLAGS.KILL_SWITCH.USE_PLAN_ID_FROM_CLIENT
  );
  const subscription = useSelector(state => state.subscription);
  const planNameForFlag = subscription?.subscription_status?.info?.plan ??
    subscription?.subscription_status?.plan_details?.name;
  const gatewayIdForFlag =
    subscription?.subscription_status?.plan_details?.gateway_id;
  const { isFeatureFlagEnable: isOabMgUpsellEnabled } =
    useFeatureFlagWithContext(FEATURE_FLAGS.RELEASE.UPSELL_OAB_MG, {
      plan: planNameForFlag ?? undefined,
    });
  const { isFeatureFlagEnable: isReactivationSinglePlanEnabled } =
    useFeatureFlagWithContext(FEATURE_FLAGS.RELEASE.REACTIVATION_SINGLE_PLAN, {
      plan: gatewayIdForFlag != null ? String(gatewayIdForFlag) : undefined,
    });
  const [billingCycle, setBillingCycle] = useState(BILLING_CYCLES.MONTHLY);

  const toggleBillingCycle = () => {
    setBillingCycle(prevCycle =>
      prevCycle === BILLING_CYCLES.MONTHLY
        ? BILLING_CYCLES.ANNUAL
        : BILLING_CYCLES.MONTHLY
    );
  };

  const cycle = billingCycle;

  const getPricingConfig = useCallback(() => {
    if (isUniversal && hasJusProcessos) {
      return (
        JUSPROCESSOS_PRICING_UNIVERSAL[processCount] ||
        JUSPROCESSOS_PRICING_UNIVERSAL[50]
      );
    }
    if (hasJusProcessos) {
      return JUSPROCESSOS_PRICING[processCount] || JUSPROCESSOS_PRICING[50];
    }
    return isUniversal ? BASE_PRICING_UNIVERSAL : BASE_PRICING;
  }, [hasJusProcessos, processCount, isUniversal]);

  const getPrices = useCallback(() => {
    const pricingConfig = getPricingConfig();
    return {
      starter: getPlanDisplayData(pricingConfig[PLAN_TYPES.STARTER], cycle),
      master: getPlanDisplayData(pricingConfig[PLAN_TYPES.MASTER], cycle),
      ultimate: getPlanDisplayData(pricingConfig[PLAN_TYPES.ULTIMATE], cycle),
    };
  }, [getPricingConfig, cycle]);

  const [planPrices, setPlanPrices] = useState(getPrices);

  useEffect(() => {
    if (isFeatureFlagEnable) return;
    setPlanPrices(getPrices());
  }, [getPrices, isFeatureFlagEnable]);

  if (!isFeatureFlagEnable) {
    return {
      billingCycle,
      toggleBillingCycle,
      planPrices,
      isReactivationSinglePlan: false,
      isLoading: !flagResolved,
    };
  }

  const isSubscriptionLoading =
    subscription?.is_subscription_status_loading ?? true;
  if (isSubscriptionLoading) {
    return {
      billingCycle,
      toggleBillingCycle,
      planPrices: getPrices(),
      isReactivationSinglePlan: false,
      isLoading: true,
    };
  }

  if (isReactivationSinglePlanEnabled) {
    const reactivationPlan = getSubscriptionPlans({
      reactivationOfferType: '2026',
    });
    const ultimate = reactivationPlan ? perfilAdapter(reactivationPlan) : null;
    if (ultimate) {
      return {
        billingCycle: BILLING_CYCLES.ANNUAL,
        toggleBillingCycle: () => {},
        planPrices: {
          starter: null,
          master: null,
          ultimate,
        },
        isReactivationSinglePlan: true,
        isLoading: false,
      };
    }
  }

  const normalizedCycle = normalizeCycleForFindPlanOptions(cycle);
  const affiliationType = isOabMgUpsellEnabled ? "caamg" : undefined;

  const plans = getSubscriptionPlans([
    {
      plan_type: 'starter',
      billingCycle: normalizedCycle,
      hasJusProcessos,
      jusprocessos_count: processCount,
      is_offer: false,
      affiliation_type: affiliationType,
    },
    {
      plan_type: 'master',
      billingCycle: normalizedCycle,
      hasJusProcessos,
      jusprocessos_count: processCount,
      is_offer: false,
      affiliation_type: affiliationType,
    },
    {
      plan_type: 'ultimate',
      billingCycle: normalizedCycle,
      hasJusProcessos,
      jusprocessos_count: processCount,
      is_offer: false,
      affiliation_type: affiliationType,
    },
  ]);

  const starterPlan = plans.find(p => p.plan_type === 'starter');
  const masterPlan = plans.find(p => p.plan_type === 'master');
  const ultimatePlan = plans.find(p => p.plan_type === 'ultimate');

  const pricingConfig = getPricingConfig();

  const newPlanPrices = {
    starter: perfilAdapter(starterPlan) || getPlanDisplayData(pricingConfig[PLAN_TYPES.STARTER], cycle),
    master: perfilAdapter(masterPlan) || getPlanDisplayData(pricingConfig[PLAN_TYPES.MASTER], cycle),
    ultimate: perfilAdapter(ultimatePlan) || getPlanDisplayData(pricingConfig[PLAN_TYPES.ULTIMATE], cycle),
  };

  return {
    billingCycle,
    toggleBillingCycle,
    planPrices: newPlanPrices,
    isReactivationSinglePlan: false,
    isLoading: false,
  };
}

export { BILLING_CYCLES, PLAN_TYPES, useSubscriptionPlans };
